import { useState } from 'react';
import type { Sharing, TrainerLink } from '../backend/types.ts';
import { BOT_NUMBER } from '../config.ts';
import { useModel } from '../state.tsx';
import { Card, Pill, ToggleRow } from '../ui/components.tsx';
import { Credits, DataInventory, PrivacyPolicy } from './Legal.tsx';
import { AvatarPicker } from './Onboarding.tsx';

type Page = 'main' | 'privacy' | 'data' | 'credits';

export function You() {
  const model = useModel();
  const [page, setPage] = useState<Page>('main');
  const [name, setName] = useState(model.profile?.full_name ?? '');
  const [confirm, setConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  if (page !== 'main') {
    return (
      <div className="screen">
        <button className="link" onClick={() => setPage('main')}>‹ You</button>
        {page === 'privacy' ? <PrivacyPolicy /> : page === 'data' ? <DataInventory /> : <Credits />}
      </div>
    );
  }

  return (
    <div className="screen">
      <h1>You</h1>
      <Card>
        <span className="eyebrow">Profile</span>
        {model.email && <p className="caption">Signed in as {model.email}</p>}
        <input className="field" value={name} aria-label="Full name" onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() && name.trim() !== model.profile?.full_name && model.updateProfile({ full_name: name.trim() })} />
        <AvatarPicker value={model.profile?.avatar ?? 'male'} onChange={(avatar) => model.updateProfile({ avatar })} />
      </Card>

      <Trainer />

      <Card>
        <span className="eyebrow">Privacy</span>
        <ToggleRow title="Health and training data" detail="Required to use Recover. Turning it off pauses the app until you turn it back on."
          checked={model.consents.has('health_data')} onChange={(v) => model.setConsent('health_data', v)} />
        <ToggleRow title="Share with my trainer" detail="Turning this off stops all digests to trainers."
          checked={model.consents.has('trainer_sharing')} onChange={(v) => model.setConsent('trainer_sharing', v)} />
        <div className="list">
          <button className="list-item" onClick={() => setPage('privacy')}>Privacy policy<span className="spacer" />›</button>
          <button className="list-item" onClick={() => setPage('data')}>What we store<span className="spacer" />›</button>
          <button className="list-item" onClick={() => setPage('credits')}>Credits<span className="spacer" />›</button>
        </div>
      </Card>

      <Card>
        {model.backend.isDemo && <p className="caption warning">Demo mode: data stays in this browser.</p>}
        <button className="btn secondary" onClick={() => model.signOut()}>Sign out</button>
        <p className="caption">Deletes your profile, sessions, ratings, check-ins, trainer links and trainer notes from our servers immediately. This cannot be undone.</p>
        <input className="field" placeholder="Type DELETE to confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          aria-label="Type DELETE to confirm" data-testid="delete-confirm" />
        <button className="btn danger" disabled={confirm !== 'DELETE' || deleting} data-testid="delete-account"
          onClick={async () => { setDeleting(true); await model.deleteAccount(); setDeleting(false); }}>
          {deleting ? 'Deleting…' : 'Delete my account and data'}
        </button>
      </Card>
    </div>
  );
}

const SHARING: [keyof Sharing, string][] = [
  ['share_muscles', 'Muscles worked'],
  ['share_ratings', 'Intensity and recovery ratings'],
  ['share_solo', 'Workouts on my own (runs, rides…)'],
  ['share_health', 'Workout details (duration, heart rate, distance)'],
];

function Trainer() {
  const model = useModel();
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const sharing = model.consents.has('trainer_sharing');
  const digits = BOT_NUMBER.replace(/\D/g, '');

  const toggle = (link: TrainerLink, key: keyof Sharing, value: boolean) => {
    const { share_muscles, share_ratings, share_solo, share_health } = link;
    void model.updateSharing(link, { share_muscles, share_ratings, share_solo, share_health, [key]: value });
  };

  return (
    <Card>
      <span className="eyebrow">Trainer</span>
      {!sharing && (
        <div className="stack">
          <h2>Sharing with a trainer is off</h2>
          <p className="caption">Turn it on to send your trainer a digest before each session and receive their notes.</p>
          <button className="btn primary" onClick={() => model.setConsent('trainer_sharing', true)}>Turn on sharing</button>
        </div>
      )}
      {model.links.map((link) => (
        <div className="stack" key={link.id}>
          <div className="row"><h2>{link.trainer_name}</h2><span className="spacer" /><Pill text="Linked" /></div>
          {SHARING.map(([key, label]) => (
            <ToggleRow key={key} title={label} checked={link[key]} onChange={(v) => toggle(link, key, v)} />
          ))}
          <button className="btn danger" onClick={() => model.revoke(link)}>Disconnect {link.trainer_name}</button>
        </div>
      ))}
      {sharing && (code ? (
        <div className="stack">
          <div className="accent" style={{ fontSize: 40, fontWeight: 700, fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em' }}
            data-testid="invite-code">{code.slice(0, 3)} {code.slice(3)}</div>
          <p className="caption">Ask your trainer to send this code to the Recover WhatsApp number {BOT_NUMBER}. It works once and expires in 7 days.</p>
          <a className="btn primary" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer"
            href={`https://wa.me/?text=${encodeURIComponent(`Hi! I use Recover to log my training. Send the code ${code} to https://wa.me/${digits}?text=${code} and you'll get my digest before our sessions.`)}`}>
            Send to my trainer on WhatsApp
          </a>
        </div>
      ) : (
        <button className="btn secondary" disabled={loading} data-testid="create-invite" onClick={async () => {
          setLoading(true); setCode((await model.createInvite()) ?? null); setLoading(false);
        }}>{loading ? 'Creating…' : model.links.length ? 'Invite another trainer' : 'Invite your trainer'}</button>
      ))}
      <p className="caption">Your trainer never gets access to the app or your account, only the WhatsApp digest. Notes they send are always visible to you.</p>
    </Card>
  );
}
